import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Autocomplete,
  AutocompleteProps,
  FormHelperText,
  InputLabel,
  Stack,
  StackProps,
  TextField,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { GetFieldIsValidAndErrorMessage, DeleteField } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import useMultipleSelectValidator, {
  MultiSelectValidatorProps,
} from "../../../Utils/Hooks/Validators/useMultipleSelectValidator";
import useSelectValidator, { SelectValidatorProps } from "../../../Utils/Hooks/Validators/useSelectValidator";

interface FormAutocompleteProps<T> extends Omit<AutocompleteProps<T, boolean, boolean, boolean, "div">, "renderInput"> {
  label?: string;
  placeholder?: string;
  inputName?: string;
  validationProps: SelectValidatorProps<T> | MultiSelectValidatorProps<T>;
  stackProps?: StackProps;
  onChange: (value: any) => void;
}

const FormAutocomplete = <T,>(props: FormAutocompleteProps<T>) => {
  const { label, validationProps, onChange, stackProps, ...otherProps } = props;
  const validateSelect = useSelectValidator<T>(validationProps as SelectValidatorProps<T>);
  const validateSelectMulti = useMultipleSelectValidator<T>(validationProps as MultiSelectValidatorProps<T>);

  const errorMessage = useAppSelector(GetFieldIsValidAndErrorMessage(validationProps.formId, validationProps.fieldId));
  const isValid = !errorMessage;
  const dispatch = useAppDispatch();

  // This works only if we update
  const handleOnChange = (event: React.SyntheticEvent<Element, Event>, value: any) => {
    if (!props.multiple) validateSelect(value);
    else validateSelectMulti(value);

    onChange && onChange(value);
  };

  const [isValidMemo, errorMessageMemo] = useMemo(() => {
    return [isValid, errorMessage];
  }, [isValid]);

  useEffect(() => {
    if (!props.value) validateSelect(undefined);
    else {
      if (!props.multiple) validateSelect(props.value.toString());
      else validateSelectMulti(props.value! as any[]);
    }
  }, [validationProps.required, props.value, props.multiple]); // Rerender when required deps changes and also when value change

  useEffect(() => {
    return () => {
      dispatch(
        DeleteField({
          formId: validationProps.formId,
          formSectionId: validationProps.formSectionId,
          fieldId: validationProps.fieldId,
        })
      );
    };
  }, []);

  return (
    <Stack {...stackProps}>
      {label && (
        <InputLabel sx={{ mb: 0 }} htmlFor={`${validationProps.fieldId}-label`} required={validationProps.required}>
          {label}
        </InputLabel>
      )}
      {/* [AC] Mystery: I dont' have any idea of why we need to set mb -2 to be correct... */}
      <Autocomplete
        sx={{ mt: -2 }}
        popupIcon={props.popupIcon ? props.popupIcon : <ExpandMoreRoundedIcon />}
        onChange={(event, value) => handleOnChange(event, value)}
        renderInput={(params) => <TextField {...params} placeholder={props.placeholder} name={props.inputName} />}
        {...otherProps}
      />

      {!isValidMemo && <FormHelperText error>{errorMessageMemo}</FormHelperText>}
    </Stack>
  );
};

export default FormAutocomplete;
